/**
 * Hooks del portal: el pegamento entre los módulos puros de `src/lib/` y
 * React. Deliberadamente flacos — acá no hay reglas de negocio, sólo ciclo de
 * vida (suscribirse al hash, pedir datos, cancelar al desmontar).
 */
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { getT } from './i18n/index';
import { ApiError } from './lib/api-client';
import { parseHash, type Route } from './lib/router';
import type { Settings, SettingsStore } from './lib/settings';

// ---------------------------------------------------------------------------
// Ruta
// ---------------------------------------------------------------------------

function subscribeToHash(listener: () => void): () => void {
  window.addEventListener('hashchange', listener);
  return () => window.removeEventListener('hashchange', listener);
}

function currentHash(): string {
  return window.location.hash;
}

export function useRoute(): Route {
  const hash = useSyncExternalStore(subscribeToHash, currentHash, () => '#/matches');
  return useMemo(() => parseHash(hash), [hash]);
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export function useSettings(store: SettingsStore): Settings {
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
}

// ---------------------------------------------------------------------------
// Carga asincrónica
// ---------------------------------------------------------------------------

export type AsyncStatus = 'idle' | 'loading' | 'ok' | 'error';

export interface AsyncState<T> {
  status: AsyncStatus;
  data: T | null;
  error: Error | null;
  /** true la primera vez; permite mostrar el dato viejo mientras se recarga. */
  first: boolean;
}

export interface AsyncResult<T> extends AsyncState<T> {
  reload: () => void;
}

/**
 * Corre `fn` cuando cambian `deps` y expone el trío carga/error/dato.
 *
 * Detalles que importan en un portal:
 *  - el resultado de un pedido viejo NUNCA pisa a uno nuevo (guarda de
 *    cancelación por `run`);
 *  - un dato ya cargado se mantiene en pantalla mientras se recarga (cambiar
 *    el filtro de sets no debería hacer parpadear la tabla entera).
 */
export function useAsync<T>(fn: () => Promise<T>, deps: readonly unknown[]): AsyncResult<T> {
  const [state, setState] = useState<AsyncState<T>>({
    status: 'idle',
    data: null,
    error: null,
    first: true,
  });
  const [nonce, setNonce] = useState(0);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    let alive = true;
    setState((prev) => ({ ...prev, status: 'loading', error: null }));
    fnRef
      .current()
      .then((data) => {
        if (!alive) return;
        setState({ status: 'ok', data, error: null, first: false });
      })
      .catch((err: unknown) => {
        if (!alive) return;
        const error = err instanceof Error ? err : new Error(String(err));
        setState((prev) => ({ status: 'error', data: prev.data, error, first: false }));
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { ...state, reload };
}

/** Mensaje mostrable de cualquier error (los `ApiError` ya vienen traducidos). */
export function errorMessage(error: Error | null): string {
  if (!error) return '';
  if (error instanceof ApiError) return error.message;
  return error.message || getT()('common.unexpectedError');
}
